"use client";

import { c as _c } from "react/compiler-runtime";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@repo/design-system/utils/cn";
import { ChevronDownIcon, Plus } from "lucide-react";
import type * as React from "react";
import { ChevronDownCompact } from "../icons/common";
type Icon = "cross" | "chevron" | "chevronFlat";
function Accordion(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <AccordionPrimitive.Root data-slot="accordion" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function AccordionItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-b last:border-b-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AccordionPrimitive.Item className={t1} data-slot="accordion-item" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function AccordionTrigger(t0) {
  const $ = _c(16);
  let children;
  let className;
  let icon;
  let iconClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      icon,
      iconClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = iconClassName;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    iconClassName = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn("flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left font-medium text-sm outline-none transition-all hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== icon || $[9] !== iconClassName) {
    t2 = <Icon icon={icon} iconClassName={iconClassName} />;
    $[8] = icon;
    $[9] = iconClassName;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== children || $[12] !== props || $[13] !== t1 || $[14] !== t2) {
    t3 = <AccordionPrimitive.Header className="flex"><AccordionPrimitive.Trigger className={t1} data-slot="accordion-trigger" {...props}>{children}{t2}</AccordionPrimitive.Trigger></AccordionPrimitive.Header>;
    $[11] = children;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
function AccordionContent(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("pt-0 pb-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[6] = children;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== t2) {
    t3 = <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down" data-slot="accordion-content" {...props}>{t2}</AccordionPrimitive.Content>;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
const Icon = t0 => {
  const $ = _c(12);
  const {
    icon,
    iconClassName
  } = t0;
  if (icon === "cross") {
    let t1;
    if ($[0] !== iconClassName) {
      t1 = cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", iconClassName);
      $[0] = iconClassName;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Plus className={t1} />;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (icon === "chevronFlat") {
    let t1;
    if ($[4] !== iconClassName) {
      t1 = cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", iconClassName);
      $[4] = iconClassName;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== t1) {
      t2 = <ChevronDownCompact className={t1} />;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== iconClassName) {
    t1 = cn("pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200", iconClassName);
    $[8] = iconClassName;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== t1) {
    t2 = <ChevronDownIcon className={t1} />;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};